/**
 * IncassoKanbanPage
 *
 * Main kanban view for incasso dossiers.
 * Follows the same structure as HelpdeskKanbanPage.
 *
 * Features:
 * - Per-column paginated kanban board (sorted by highest amount)
 * - Stats bar (clickable KPIs)
 * - Phase transition dialog on specific drops
 * - Click card → navigates to /rental/incasso/dossier/:id (full detail page)
 * - Snooze dialog (via card quick actions or detail page)
 * - Reuses existing incasso modals (Action, Legal, PaymentPlan)
 * - View toggle (Lijst / Kanban)
 */

import { memo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
import { ListBulletsIcon, KanbanIcon, ArrowSquareOut, Info, ArrowsClockwise, DownloadSimple } from '@phosphor-icons/react';
import { PixelLogo } from 'shared/components/PixelLogo';
import { FilterBar } from 'shared/components/filterBar';
import { IncassoStatsBar } from '../components/IncassoStatsBar';
import { IncassoDebtChart } from '../components/IncassoDebtChart';
import { IncassoKanbanView } from '../components/IncassoKanbanView';
import { SnoozeDialog } from '../components/SnoozeDialog';
import { PhaseTransitionDialog } from '../components/PhaseTransitionDialog';
import { useIncassoShared } from '../shared/hooks/useIncassoShared';
import { useIncassoKanbanData } from '../shared/hooks/useIncassoKanbanData';
import { incassoPhaseColumns, getPhaseLabel } from '../configs/incassoPhaseConfig';
import { IncassoPhase, IncassoBeoordeling, IncassoTicket } from '../shared/incassoTypes';
import {
  IncassoActionModal,
  LegalProcedureModal,
  PaymentPlanModal,
} from 'shared/components/rental/sections/incasso/components';

// ─── Phases loaded in kanban (all 5) ─────────────────────────────────────────
const ALL_PHASES = incassoPhaseColumns.map((c) => c.id);

// ─── Helper: build a minimal invoice-compatible object for legacy modals ──────
const ticketToInvoice = (ticket: IncassoTicket | null): any => {
  if (!ticket) return null;
  return {
    id: Number(ticket.id),
    name: '',
    amount_residual: Number(ticket.cachedTotalDebt) || 0,
    partner_id: [ticket.odooPartnerId, ticket.odooPartnerDisplayName],
    invoice_partner_display_name: ticket.odooPartnerDisplayName,
    contact: ticket.contact
      ? {
          id: ticket.contact.id,
          firstName: ticket.contact.firstName,
          lastName: ticket.contact.lastName,
          phone_mobile: ticket.contact.phone_mobile || undefined,
          phone_direct: ticket.contact.phone_direct || undefined,
          notes: ticket.contact.notes,
          email: ticket.contact.email || '',
          jobTitle: '',
        }
      : ticket.contactId
        ? { id: ticket.contactId }
        : null,
    company: ticket.company
      ? {
          id: ticket.company.id,
          name: ticket.company.name,
          phone_direct: ticket.company.phone_direct,
          notes: ticket.company.notes,
        }
      : ticket.companyId
        ? { id: ticket.companyId }
        : null,
    unit: null,
    x_studio_sub_unit: '',
    invoice_date: null,
    invoice_date_due: null,
    invoice_days_due: String(ticket.cachedMaxDaysOverdue || 0),
    analytic_account: ticket.cachedBuildingName || '',
    incassoActionCount: ticket.incassoActionCount || 0,
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const IncassoKanbanPageComponent = () => {
  const navigate = useNavigate();

  const state = useIncassoShared('kanban');

  const {
    searchText,
    setSearchText,
    isRestoringFromURL,
    apiFilters,
    stats,
    hiddenKanbanColumns,
    kanbanFontSize,
    handleToggleColumnVisibility,
    handleIncreaseFontSize,
    handleDecreaseFontSize,
    filterBarSections,
    filterBarChips,
    handleFiltersFromURL,
    handlePhaseChange,
    handleBeoordelingChange,
    handleSnooze,
    handleUnsnooze,
    snoozeModal,
    setSnoozeModal,
    phaseTransitionPending,
    setPhaseTransitionPending,
    actionModal,
    setActionModal,
    legalModal,
    setLegalModal,
    paymentPlanModal,
    setPaymentPlanModal,
    fetchStats,
    totalUnpaidRent,
    hasSynced,
    handlePaymentPlanStatsClick,
    handleLegalProcedureStatsClick,
    handleSnoozedStatsClick,
    handleIncassoViaDerdeStatsClick,
    isRefreshing,
    handleRefresh,
    isExporting,
    handleExportToExcel,
  } = state;

  // Per-column kanban data — wait for sync before fetching
  const kanbanData = useIncassoKanbanData({
    phases: ALL_PHASES,
    filters: apiFilters,
    enabled: hasSynced,
  });

  const {
    allTickets,
    columnPagination,
    moveTicketLocally,
    updateTicketLocally,
    refreshAll,
  } = kanbanData;

  // ─── Phase transition dialog handling ────────────────────────────────────

  const handlePhasePending = useCallback(
    (payload: {
      ticket: IncassoTicket;
      fromPhase: IncassoPhase;
      toPhase: IncassoPhase;
    }) => {
      setPhaseTransitionPending(payload);
    },
    [setPhaseTransitionPending],
  );

  const handlePhaseTransitionConfirm = useCallback(
    async (createRelated: boolean) => {
      if (!phaseTransitionPending) return;
      const { ticket, fromPhase, toPhase } = phaseTransitionPending;

      // Optimistic: move ticket visually + toast immediately
      moveTicketLocally(String(ticket.id), fromPhase, toPhase);
      toast.success(`Dossier verplaatst naar "${getPhaseLabel(toPhase)}"`);
      setPhaseTransitionPending(null);

      // Persist to backend
      await handlePhaseChange(String(ticket.id), toPhase);

      // Then open related modal if user chose to create related item
      if (createRelated) {
        if (toPhase === IncassoPhase.MINNELIJKE_REGELING) {
          setPaymentPlanModal(ticket);
        } else if (toPhase === IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN) {
          setLegalModal(ticket);
        }
      }

      await refreshAll();
      fetchStats();
    },
    [
      phaseTransitionPending,
      moveTicketLocally,
      handlePhaseChange,
      setPhaseTransitionPending,
      setPaymentPlanModal,
      setLegalModal,
      refreshAll,
      fetchStats,
    ],
  );

  const handlePhaseTransitionCancel = useCallback(() => {
    setPhaseTransitionPending(null);
  }, [setPhaseTransitionPending]);

  // ─── Beoordeling change — optimistic update + API call ──────────────────
  const handleBeoordelingOptimistic = useCallback(
    async (ticket: IncassoTicket, beoordeling: IncassoBeoordeling | null, reden?: string) => {
      // Optimistic: update card immediately
      updateTicketLocally(String(ticket.id), {
        beoordeling,
        beoordelingReden: reden ?? null,
      });

      try {
        await handleBeoordelingChange(ticket, beoordeling, reden);
      } catch {
        // Rollback on failure
        updateTicketLocally(String(ticket.id), {
          beoordeling: ticket.beoordeling,
          beoordelingReden: ticket.beoordelingReden,
        });
      }
    },
    [updateTicketLocally, handleBeoordelingChange],
  );

  // ─── Ticket click — navigate to dossier detail page ────────────────────
  const handleTicketClick = useCallback(
    (ticket: IncassoTicket) => {
      navigate(`/rental/incasso/dossier/${ticket.id}`);
    },
    [navigate],
  );

  // ─── Deep-link: ?openUnitOdooId=<unit.odooId> auto-opens the dossier ─────
  // Used by the watchlist "Openstand" link to jump straight into a debtor's
  // dossier filtered to the relevant building. Runs once after tickets load.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (!hasSynced || allTickets.length === 0) return;

    try {
      const params = new URLSearchParams(window.location.search);
      const target = params.get('openUnitOdooId');
      if (!target) return;

      const match = allTickets.find((t) => t.cachedBuildingName === target);
      if (match) {
        navigate(`/rental/incasso/dossier/${match.id}`);
      } else {
        toast.info('Geen dossier gevonden voor deze unit binnen de huidige filters.');
      }

      hasAutoOpenedRef.current = true;
      params.delete('openUnitOdooId');
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}`;
      window.history.replaceState({}, '', url);
    } catch (err) {
      console.error('Failed to auto-open incasso dossier from URL:', err);
    }
  }, [hasSynced, allTickets, navigate]);

  // ─── Refresh after modal actions ──────────────────────────────────────────
  const handleModalClose = useCallback(async () => {
    await refreshAll();
    fetchStats();
  }, [refreshAll, fetchStats]);

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0 min-h-screen">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Incasso Dossiers</h1>
              <p className="text-xs text-neutral-secondary-mid mt-1 flex items-center gap-1">
                <Info size={12} weight="fill" className="shrink-0" />
                Dossiers verschijnen pas na 15 dagen te laat. Gesnoozede dossiers worden automatisch weer zichtbaar na de snoozedatum.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Refresh button + last snapshot */}
              <div className="flex items-center gap-2">
                {stats?.lastSnapshotAt && (
                  <span className="text-[11px] text-neutral-secondary-mid whitespace-nowrap">
                    Laatste snapshot:{' '}
                    {new Date(stats.lastSnapshotAt).toLocaleString('nl-BE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
                <button
                  onClick={async () => {
                    await handleRefresh();
                    refreshAll();
                  }}
                  disabled={isRefreshing}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium transition-colors',
                    isRefreshing
                      ? 'cursor-not-allowed text-neutral-400'
                      : 'text-neutral-700 hover:bg-neutral-50',
                  )}
                >
                  <ArrowsClockwise
                    size={14}
                    className={isRefreshing ? 'animate-spin' : ''}
                  />
                  {isRefreshing ? 'Vernieuwen...' : 'Vernieuwen'}
                </button>
              </div>

              {/* Quick links */}
              <a
                href="https://territoriale-bevoegdheid.just.fgov.be"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                Vredegerecht
                <ArrowSquareOut size={14} />
              </a>
              <a
                href="https://access.eservices.just.fgov.be/edeposit.nl/login"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                E-Deposit
                <ArrowSquareOut size={14} />
              </a>

              {/* Export to Excel */}
              <button
                onClick={handleExportToExcel}
                disabled={isExporting}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium transition-colors',
                  isExporting
                    ? 'cursor-not-allowed text-neutral-400'
                    : 'text-neutral-700 hover:bg-neutral-50',
                )}
                title="Exporteer gefilterde dossiers naar Excel"
              >
                <DownloadSimple size={14} />
                <span className="hidden sm:inline">
                  {isExporting ? 'Exporteren...' : 'Exporteer naar Excel'}
                </span>
              </button>

              {/* View toggle */}
              <div className="border-input bg-background flex items-center gap-1 rounded-lg border p-1 shadow-sm">
                <button
                  onClick={() => navigate('/rental/incasso/list')}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                  aria-label="Lijstweergave"
                  title="Lijstweergave"
                >
                  <ListBulletsIcon size={18} weight="regular" />
                  <span className="hidden sm:inline">Lijst</span>
                </button>
                <button
                  onClick={() => navigate('/rental/incasso/kanban')}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    'bg-moss-primary text-white shadow-sm',
                  )}
                  aria-label="Kanbanweergave"
                  title="Kanbanweergave"
                >
                  <KanbanIcon size={18} weight="fill" />
                  <span className="hidden sm:inline">Kanban</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* ── Stats bar ───────────────────────────────────────────────────── */}
        {stats && (
          <div className="px-6 pb-4">
            <IncassoStatsBar
              totalUnpaidRent={totalUnpaidRent}
              totalOutstanding={stats.totalOutstanding}
              activeCases={stats.activeCases}
              snoozedCases={stats.snoozedCases}
              paymentPlanAmount={stats.paymentPlanAmount}
              legalProcedureAmount={stats.legalProcedureAmount}
              snoozedAmount={stats.snoozedAmount}
              incassoViaDerdeAmount={stats.incassoViaDerdeAmount}
              onPaymentPlanClick={handlePaymentPlanStatsClick}
              onLegalProcedureClick={handleLegalProcedureStatsClick}
              onSnoozedClick={handleSnoozedStatsClick}
              onIncassoViaDerdeClick={handleIncassoViaDerdeStatsClick}
            />
          </div>
        )}

        {/* ── Debt evolution chart ──────────────────────────────────────────── */}
        <div className="px-6 pb-4">
          <IncassoDebtChart />
        </div>

        {/* ── Kanban board ─────────────────────────────────────────────────── */}
        <div className="px-6 pb-32">
          {stats && !stats.lastSnapshotAt ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-neutral-200 bg-white py-16 px-6">
              <ArrowsClockwise size={32} className="text-neutral-secondary-mid mb-3" />
              <p className="text-sm font-medium text-neutral-700 mb-1">
                Nog geen snapshot data beschikbaar
              </p>
              <p className="text-xs text-neutral-secondary-mid mb-4">
                Klik op &quot;Vernieuwen&quot; om de eerste snapshot te maken. Daarna worden de dossiers hier getoond.
              </p>
              <button
                onClick={async () => {
                  await handleRefresh();
                  refreshAll();
                }}
                disabled={isRefreshing}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2 text-sm font-medium transition-colors',
                  isRefreshing
                    ? 'cursor-not-allowed text-neutral-400'
                    : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                <ArrowsClockwise
                  size={14}
                  className={isRefreshing ? 'animate-spin' : ''}
                />
                {isRefreshing ? 'Vernieuwen...' : 'Vernieuwen'}
              </button>
            </div>
          ) : !hasSynced ? (
            <PixelLogo size={360} text="Dossiers synchroniseren..." fullPage />
          ) : (
            <IncassoKanbanView
              tickets={allTickets}
              onPhaseChange={handlePhaseChange}
              onMoveTicketLocally={moveTicketLocally}
              onTicketClick={handleTicketClick}
              onBeoordelingChange={handleBeoordelingOptimistic}
              onPhasePending={handlePhasePending}
              columnPagination={columnPagination as any}
              hiddenColumns={hiddenKanbanColumns}
              onToggleColumnVisibility={handleToggleColumnVisibility}
              fontSize={kanbanFontSize}
              onIncreaseFontSize={handleIncreaseFontSize}
              onDecreaseFontSize={handleDecreaseFontSize}
            />
          )}
        </div>
      </div>

      {/* ── FilterBar ─────────────────────────────────────────────────────── */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={(value) => {
          if (!isRestoringFromURL) {
            setSearchText(value);
          }
        }}
        searchPlaceholder="Zoek dossiers op naam, gebouw..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={true}
        urlParamPrefix=""
        onFiltersFromURL={handleFiltersFromURL}
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* ── Snooze Dialog ─────────────────────────────────────────────────── */}
      <SnoozeDialog
        isOpen={snoozeModal.open}
        onClose={() => setSnoozeModal({ open: false, ticket: null })}
        ticketLabel={snoozeModal.ticket?.odooPartnerDisplayName}
        currentSnooze={
          snoozeModal.ticket?.isSnoozed && snoozeModal.ticket?.snoozeUntil
            ? {
                until: snoozeModal.ticket.snoozeUntil,
                reason: snoozeModal.ticket.snoozeReason ?? '',
              }
            : null
        }
        onConfirm={(snoozeUntil, reason) => {
          if (snoozeModal.ticket) {
            handleSnooze(snoozeModal.ticket, snoozeUntil, reason).then(() => handleModalClose());
          }
        }}
        onUnsnooze={() => {
          if (snoozeModal.ticket) {
            handleUnsnooze(snoozeModal.ticket).then(() => handleModalClose());
          }
        }}
      />

      {/* ── Phase Transition Dialog ───────────────────────────────────────── */}
      {phaseTransitionPending && (
        <PhaseTransitionDialog
          isOpen={true}
          targetPhase={phaseTransitionPending.toPhase}
          ticketLabel={phaseTransitionPending.ticket.odooPartnerDisplayName}
          isBackward={false}
          onConfirm={handlePhaseTransitionConfirm}
          onCancel={handlePhaseTransitionCancel}
        />
      )}

      {/* ── Legacy modals (Action, Legal, PaymentPlan) ────────────────────── */}
      <IncassoActionModal
        isOpen={actionModal !== null}
        onClose={() => {
          setActionModal(null);
          handleModalClose();
        }}
        invoice={ticketToInvoice(actionModal)}
        onActionCreated={() => {
          /* stats will refresh on close */
        }}
      />

      <LegalProcedureModal
        isOpen={legalModal !== null}
        onClose={() => {
          setLegalModal(null);
          handleModalClose();
        }}
        invoice={ticketToInvoice(legalModal)}
        onProceduresChanged={handleModalClose}
      />

      <PaymentPlanModal
        isOpen={paymentPlanModal !== null}
        onClose={() => {
          setPaymentPlanModal(null);
          handleModalClose();
        }}
        invoice={ticketToInvoice(paymentPlanModal)}
        onPaymentPlansChanged={handleModalClose}
      />
    </>
  );
};

export const IncassoKanbanPage = memo(IncassoKanbanPageComponent);
